import type { IconColor, IconName } from "@platform/design-system";
import type {
  CensusField,
  CheckStatus,
  CoverageType,
} from "./hooks/useCensusValidation";

/** What each checklist field is called on screen. */
export const FIELD_LABELS: Readonly<Record<CensusField, string>> = {
  dateOfBirth: "Date of birth",
  gender: "Gender",
  elections: "Elections",
  class: "Class",
  homeZip: "Home ZIP",
  salary: "Salary",
  occupation: "Job title / occupation",
  electedVolumes: "Elected volumes",
  workZip: "Work ZIP",
  workState: "Work state",
  w2Employees: "W-2 employees",
};

/** What each coverage is called where a line counts more than one. */
export const COVERAGE_LABELS: Readonly<Record<CoverageType, string>> = {
  dental: "Dental",
  vision: "Vision",
  std: "STD",
  ltd: "LTD",
  basicLife: "Basic life",
  voluntaryLife: "Voluntary life",
  adnd: "Voluntary AD&D",
};

/**
 * How each status reads, and how it looks.
 *
 * Neither "unread" nor "notInCensus" is called missing, and they are not the
 * same as each other. Unread means we do not read that column yet, so the gap
 * is ours and worth closing. In census means no census carries the field at
 * all, so there is nothing to chase a broker for and nothing to fix.
 */
export const STATUS_LABELS: Readonly<Record<CheckStatus, string>> = {
  complete: "Complete",
  partial: "Partial",
  missing: "Missing",
  unread: "Not read yet",
  notInCensus: "Collected on the RFP, not the census",
};

/** The catalog glyph and color each status reads as. */
export const STATUS_ICONS: Readonly<
  Record<CheckStatus, { name: IconName; color: IconColor }>
> = {
  complete: { name: "success", color: "success" },
  partial: { name: "warning", color: "warning" },
  missing: { name: "error", color: "error" },
  unread: { name: "remove", color: "disabled" },
  notInCensus: { name: "remove", color: "disabled" },
};

/**
 * How a census source reads to an analyst. The stored values are snake_case
 * and name our own ingestion paths, which is not what a page should show.
 */
export const SOURCE_LABELS: Readonly<Record<string, string>> = {
  broker_upload: "uploaded by the broker",
  analyst_upload: "uploaded by an analyst",
  employee_navigator: "pulled from Employee Navigator",
};
